import { Focus, Search, TriangleAlert, Workflow, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { GraphDepth, GraphPerspective } from '@/frontend/features/Fde/ontologyGraphPerspective'
import type { FdeCopy, ModelElement, ProcessMap } from '@/frontend/features/Fde/types'

interface Props {
  query: string
  perspective: GraphPerspective
  depth: GraphDepth
  anchors: ModelElement[]
  manualFocus: boolean
  showGaps: boolean
  gapCount: number
  visibleCount: number
  totalCount: number
  processMaps: ProcessMap[]
  copy: FdeCopy
  onQueryChange: (value: string) => void
  onPerspectiveChange: (value: GraphPerspective) => void
  onDepthChange: (value: GraphDepth) => void
  onRemoveAnchor: (id: string) => void
  onShowGaps: () => void
  onLeaveGaps: () => void
  onSelectProcess: (process: ProcessMap) => void
}

const perspectives: GraphPerspective[] = ['processes', 'people', 'organization', 'systems', 'value', 'free']
const depths: GraphDepth[] = [1, 2, 'all']

export default function OntologyGraphToolbar(props: Props) {
  return (
    <div className="shrink-0 border-b border-border bg-background" data-testid="ontology-graph-toolbar">
      <div className="flex items-center gap-2 overflow-x-auto px-3 py-2">
        <div className="relative min-w-64 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={props.query} onChange={(event) => props.onQueryChange(event.target.value)} placeholder={props.copy.workspace.graph_search} className="h-8 pl-8 text-xs" />
        </div>
        <Select value={props.perspective} disabled={props.showGaps} onValueChange={(value) => props.onPerspectiveChange(value as GraphPerspective)}>
          <SelectTrigger size="sm" className="w-48 shrink-0 text-xs" aria-label={props.copy.workspace.graph_perspective}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {perspectives.map((value) => <SelectItem key={value} value={value}>{props.copy.workspace[`graph_perspective_${value}`]}</SelectItem>)}
          </SelectContent>
        </Select>
        {!props.showGaps && props.perspective === 'processes' && props.processMaps.length > 0 && <Select onValueChange={(slug) => {
          const process = props.processMaps.find((item) => item.slug === slug)
          if (process) props.onSelectProcess(process)
        }}>
          <SelectTrigger size="sm" className="w-56 shrink-0 text-xs" aria-label={props.copy.workspace.process_select} data-testid="ontology-toolbar-process-select">
            <Workflow className="size-3.5 text-muted-foreground" />
            <SelectValue placeholder={props.copy.workspace.process_select} />
          </SelectTrigger>
          <SelectContent>{props.processMaps.map((process) => <SelectItem key={process.slug} value={process.slug}>{process.name}</SelectItem>)}</SelectContent>
        </Select>}
        {!props.showGaps && <div className="flex items-center rounded-md border border-border p-0.5" aria-label={props.copy.workspace.graph_depth}>
          {depths.map((value) => <button key={value} type="button" onClick={() => props.onDepthChange(value)} aria-pressed={props.depth === value} className={`rounded px-2 py-1 text-[11px] font-medium ${props.depth === value ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {props.copy.workspace[`graph_depth_${value}`]}
          </button>)}
        </div>}
        {props.gapCount > 0 && <Button type="button" size="sm" variant={props.showGaps ? 'secondary' : 'ghost'} onClick={props.showGaps ? props.onLeaveGaps : props.onShowGaps}>
          <TriangleAlert />{props.showGaps ? props.copy.workspace.graph_back_to_focus : `${props.copy.workspace.graph_gaps} ${props.gapCount}`}
        </Button>}
      </div>

      <div className="flex min-h-9 items-center gap-2 border-t border-border/60 bg-muted/15 px-3 py-1.5">
        <Focus className="size-3.5 shrink-0 text-primary" />
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {props.showGaps ? props.copy.workspace.graph_gaps : props.copy.workspace.graph_focus}
        </span>
        {!props.showGaps && <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto">
          {props.anchors.length === 0 ? <span className="py-1 text-[11px] text-muted-foreground">{props.copy.workspace.graph_focus_empty}</span> : props.anchors.map((anchor) => (
            <span key={anchor.id} className="flex shrink-0 items-center gap-1 rounded-md border border-primary/20 bg-primary/5 px-2 py-1 text-[10px] font-medium text-foreground">
              {anchor.name}
              {props.manualFocus && <button type="button" onClick={() => props.onRemoveAnchor(String(anchor.id))} aria-label={`${props.copy.workspace.graph_remove_focus}: ${anchor.name}`} className="rounded-sm text-muted-foreground hover:text-foreground"><X className="size-3" /></button>}
            </span>
          ))}
        </div>}
        <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {props.visibleCount} {props.copy.workspace.graph_of} {props.totalCount}
        </span>
      </div>
    </div>
  )
}
